import React, { useRef, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import GoogleAutoCompleteField from "../../../GoogleAutoCompleteField";
import Cleave from "cleave.js/react";
import SelectState from "./input-elements/SelectState";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("The First Name Field is required"),
    last_name: Yup.string().required("The Last Name Field is required"),
    dba_name: Yup.string().required("The DBA name field is required"),
    dba_street: Yup.string().required("The DBA street field is required"),
    dba_city: Yup.string().required("The DBA city field is required"),
    dba_state: Yup.string().required("The DBA state field is required"),
    dba_zip: Yup.string()
        .matches(/^\d{5}$/, "Zip Code must be 5 digits")
        .required("DBA Zip Code is required"),
    postal_code: Yup.string()
        .matches(
            /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
            "Postal Code must follow the Canadian format (ANA NAN)",
        )
        .required("Postal Code is required"),
    email: Yup.string()
        .email("Invalid email")
        .required("The DBA Email Field is required"),
    mobile: Yup.string()
        .required("The DBA Mobile Phone Field is required")
        .matches(/^\d{3}-\d{3}-\d{4}$/, "Invalid mobile number format"),
});

const ContactInformation = ({
    form_data,
    setFormDataByKeyValue,
    setAddressByGoogleAPI,
    errors,
    setErrors,
    country,
}) => {
    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, { [e.target.name]: e.target.value })
            .then((e) => {
                // nothing here
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    const setAddress = (place) => {
        const addressComponents = place.address_components;
        const address1 = addressComponents.find((component) =>
            component.types.includes("street_number"),
        )?.long_name;
        const address2 = addressComponents.find((component) =>
            component.types.includes("route"),
        )?.long_name;
        const city = addressComponents.find((component) =>
            component.types.includes("locality"),
        )?.long_name;
        const state = addressComponents.find((component) =>
            component.types.includes("administrative_area_level_1"),
        )?.long_name;
        const postalCode = addressComponents.find((component) =>
            component.types.includes("postal_code"),
        )?.long_name;

        const address = {
            dba_street:
                (typeof address1 != "undefined" ? address1 : "") +
                " " +
                (typeof address2 != "undefined" ? address2 : ""),
            dba_city: city ? city : "",
            dba_state: state,
            dba_zip: postalCode,
        };
        setAddressByGoogleAPI(address);
    };

    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const dbaNameRef = useRef(null);
    const dbaStreetRef = useRef(null);
    const dbaCityRef = useRef(null);
    const dbaStateRef = useRef(null);
    const dbaZipRef = useRef(null);
    const emailRef = useRef(null);
    const mobileRef = useRef(null);

    return (
        <div className="mt-2r">
            <h6 className="sub-heading">Contact Information</h6>
            <div className="container-flexible">
                <div className="w-100">
                    <div className="floating-form">
                        <Form.Group size="lg" controlId="first_name">
                            <Form.Control
                                placeholder="Owner First Name"
                                type="text"
                                name="first_name"
                                value={form_data.first_name}
                                onChange={handleOnChange}
                                className="input-control"
                                ref={firstNameRef}
                            />
                            <Form.Label className="floating-label">
                                Owner First Name
                            </Form.Label>
                        </Form.Group>
                        {errors?.first_name &&
                            Array.isArray(errors.first_name) &&
                            errors.first_name.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>
                <div className="w-100">
                    <div className="floating-form">
                        <Form.Group size="lg" controlId="last_name">
                            <Form.Control
                                placeholder="Owner Last Name"
                                type="text"
                                name="last_name"
                                value={form_data.last_name}
                                onChange={handleOnChange}
                                className="input-control"
                                ref={lastNameRef}
                            />
                            <Form.Label className="floating-label">
                                Owner Last Name
                            </Form.Label>
                        </Form.Group>
                        {errors?.last_name &&
                            Array.isArray(errors.last_name) &&
                            errors.last_name.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>
            </div>

            <div className="floating-form dba-field-form">
                <Form.Group size="lg" controlId="dba_name">
                    <Form.Control
                        placeholder="DBA Name"
                        type="text"
                        name="dba_name"
                        value={form_data.dba_name}
                        onChange={handleOnChange}
                        className="input-control"
                        ref={dbaNameRef}
                    />
                    <Form.Label className="floating-label">DBA Name</Form.Label>
                </Form.Group>
                {errors?.dba_name &&
                    Array.isArray(errors.dba_name) &&
                    errors.dba_name.map((error) => (
                        <span className="error-message">{error}</span>
                    ))}
            </div>

            <div className="container-flexible">
                <div className="w-100">
                    <div className="floating-form">
                        <GoogleAutoCompleteField
                            className="input-control"
                            name="dba_street"
                            placeholder="DBA Address"
                            value={form_data.dba_street}
                            onPlaceSelected={setAddress}
                            onChange={handleOnChange}
                        />
                        {errors?.dba_street &&
                            Array.isArray(errors.dba_street) &&
                            errors.dba_street.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>
                <div className="w-100">
                    <div className="floating-form">
                        <Form.Group size="lg" controlId="dba_city">
                            <Form.Control
                                placeholder={
                                    country == "en" ? "DBA City" : "Province"
                                }
                                type="text"
                                name="dba_city"
                                value={form_data.dba_city}
                                onChange={handleOnChange}
                                className="input-control"
                                ref={dbaCityRef}
                            />
                            <Form.Label className="floating-label">
                                {country == "en" ? "DBA City" : "Province"}
                            </Form.Label>
                        </Form.Group>
                        {errors?.dba_city &&
                            Array.isArray(errors.dba_city) &&
                            errors.dba_city.map((error) => (
                                <span className="error-message">
                                    {country == "en"
                                        ? error
                                        : "The DBA Province field is required"}
                                </span>
                            ))}
                    </div>
                </div>
            </div>

            <div className="container-flexible">
                <div className="w-100">
                    <div className="floating-form">
                        <SelectState
                            label="DBA State"
                            name="dba_state"
                            onSelect={handleOnChange}
                            value={form_data.dba_state}
                        />
                        {errors?.dba_state &&
                            Array.isArray(errors.dba_state) &&
                            errors.dba_state.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>

                {country == "en" ? (
                    <div className="w-100">
                        <div className="floating-form">
                            <Form.Group size="lg" controlId="dba_zip">
                                <Cleave
                                    options={{
                                        blocks: [5],
                                        delimiter: "",
                                        numericOnly: true,
                                    }}
                                    required
                                    placeholder=""
                                    name="dba_zip"
                                    value={form_data.dba_zip}
                                    onChange={handleOnChange}
                                    type="text"
                                    className={`form-control input-control`}
                                    data-nid-target="dba_zip"
                                />
                                <Form.Label className="floating-label">
                                    DBA ZIP Code
                                </Form.Label>
                            </Form.Group>
                            {errors?.dba_zip &&
                                Array.isArray(errors.dba_zip) &&
                                errors.dba_zip.map((error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                    </div>
                ) : (
                    <div className="w-100">
                        <div className="floating-form">
                            <Form.Group size="lg" controlId="postal_code">
                                <Cleave
                                    id="postal_code"
                                    key={"postal_code"}
                                    options={{
                                        blocks: [3, 3],
                                        delimiter: " ",
                                        // numericOnly: true,
                                    }}
                                    required
                                    placeholder=""
                                    name="postal_code"
                                    value={form_data.postal_code}
                                    onChange={(e) =>
                                        handleOnChange({
                                            target: {
                                                name: "postal_code",
                                                value: e.target.value,
                                            },
                                        })
                                    }
                                    type="text"
                                    className={`form-control input-control`}
                                    data-nid-target="postal_code"
                                />
                                <Form.Label className="floating-label">
                                    Postal Code
                                </Form.Label>
                            </Form.Group>
                            {errors?.postal_code &&
                                Array.isArray(errors.postal_code) &&
                                errors.postal_code.map((error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="container-flexible">
                <div className="w-100">
                    <div className="floating-form">
                        <Form.Group size="lg" controlId="email">
                            <Form.Control
                                placeholder="Business Email"
                                type="text"
                                name="email"
                                value={form_data.email}
                                onChange={handleOnChange}
                                className="input-control"
                                ref={emailRef}
                            />
                            <Form.Label className="floating-label">
                                Business Email
                            </Form.Label>
                        </Form.Group>
                        {errors?.email &&
                            Array.isArray(errors.email) &&
                            errors.email.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>
                <div className="w-100">
                    <div className="floating-form">
                        <Form.Group size="lg" controlId="mobile">
                            <Cleave
                                options={{
                                    blocks: [3, 3, 4],
                                    delimiter: "-",
                                    numericOnly: true,
                                }}
                                required
                                placeholder=""
                                name="mobile"
                                value={form_data.mobile}
                                onChange={handleOnChange}
                                type="text"
                                className={`form-control input-control`}
                                data-nid-target="mobile"
                                ref={mobileRef}
                            />
                            <Form.Label className="floating-label">
                                Mobile Phone
                            </Form.Label>
                        </Form.Group>
                        {errors?.mobile &&
                            Array.isArray(errors.mobile) &&
                            errors.mobile.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactInformation;
